<template>
  <div id="checkout-page" v-if="storeDetails">
    <b-overlay :show="estoreIsLoadingOverlay" rounded="sm">
      <main
        :class="{ 'content py-4 mt-4': !isMobile, 'shadow-none': isMobile }"
      >
        <b-form
          @submit.prevent="onProceedCheckout"
          enctype="multipart/form-data"
          method="POST"
          id="checkout-form"
        >
          <div :class="{ 'container mb-60': !isMobile }">
            <div
              :class="{
                row: !isMobile,
                'd-flex flex-column-reverse flex-lg-row': isMobile
              }"
            >
              <div :class="{ 'col-lg-7': !isMobile }">
                <div
                  :class="[
                    'checkout-details py-3',
                    { 'shadow-none': isMobile }
                  ]"
                >
                  <CheckoutDetailsHeader ref="contactInformation">
                    Contact Information
                  </CheckoutDetailsHeader>
                  <div class="checkout-details-body">
                    <b-form-group
                      label="Email Address"
                      label-class="checkout-form-label checkout-form-required"
                      label-for="ci-emailAddress"
                    >
                      <b-form-input
                        id="ci-emailAddress"
                        v-model="contact_info.email"
                        required>
                      </b-form-input>
                    </b-form-group>
                  </div>
                  <CheckoutDetailsHeader ref="deliveryDetails">
                    Delivery Details
                  </CheckoutDetailsHeader
                  >
                  <div class="checkout-details-body">
                    <b-row>
                      <b-col lg="6">
                        <b-form-group
                          label="First Name"
                          label-class="checkout-form-label checkout-form-required"
                          label-for="dd-firstName"
                        >
                          <b-form-input
                            id="dd-firstName"
                            placeholder="Enter First Name"
                            required
                            v-model="contact_info.first_name"
                          >
                          </b-form-input>
                        </b-form-group>
                      </b-col>
                      <b-col lg="6">
                        <b-form-group
                          label="Last Name"
                          label-class="checkout-form-label checkout-form-required"
                          label-for="dd-lastName"
                        >
                          <b-form-input
                            id="dd-lastName"
                            placeholder="Enter Last Name"
                            required
                            v-model="contact_info.last_name"
                          >
                          </b-form-input>
                        </b-form-group>
                      </b-col>
                      <b-col lg="12">
                        <b-form-group
                          label="Location"
                          label-class="checkout-form-label"
                          label-for="dd-location"
                        >
                          <div class="form-control enter-location-container d-flex align-items-center justify-content-between py-4 px-1">
                            <span class="px-2">
                              <img src="@/assets/images/pin-fill.svg" alt="pin-icon">
                            </span>
                            <gmap-autocomplete
                              id="dd-location"
                              placeholder="Enter Location"
                              @place_changed="setPlace"
                              class="border-0 outline-0 flex-grow-1 checkout-form-required"
                              :options="{
                                componentRestrictions: { country: 'ph' }
                              }"
                              ref="address"
                              :value="enteredLocation"
                              required
                            >
                            </gmap-autocomplete> 
                          </div>
                          <span style="color: red;" v-if="invalidLocation">You entered an invalid location.</span>
                        </b-form-group>
                      </b-col>
                      <b-col lg="6">
                        <b-form-group
                          label="House/Unit/Floor Number"
                          label-class="checkout-form-label checkout-form-required"
                          label-for="locationInput"
                        >
                          <b-form-input
                            placeholder="Enter Details"
                            v-model="house"
                            required
                            maxlength="100"
                          >
                          </b-form-input>
                        </b-form-group>
                      </b-col>
                      <b-col lg="6">
                        <b-form-group
                          label="Village/Building Name"
                          label-class="checkout-form-label checkout-form-required"
                          label-for="businessName"
                        >
                          <b-form-input
                            placeholder="Enter Details"
                            id="businessName"
                            v-model="business"
                            required
                            maxlength="30"
                          >
                          </b-form-input>
                        </b-form-group>
                      </b-col>
                      <b-col lg="12">
                        <b-form-group
                          label="Delivery Instructions"
                          label-class="checkout-form-label"
                          label-for="notes"
                        >
                          <b-form-input
                            id="notes"
                            placeholder="Enter Notes"
                            v-model="notes"
                            maxlength="250"
                          >
                          </b-form-input>
                        </b-form-group>
                      </b-col>
                      <b-col>
                        <b-form-group
                          label="Mobile Number"
                          label-class="checkout-form-label checkout-form-required"
                          label-for="mobile-number">
                          <b-input-group prepend="+63">
                            <b-form-input
                              required
                              v-model="contact_info.mobile_number"
                              maxlength="10"
                              minlength="10"
                              @keydown="isNumber($event)">
                            </b-form-input>
                          </b-input-group>
                        </b-form-group>
                      </b-col>
                    </b-row>
                  </div>
                  <div
                    id="instruction-container"
                    v-if="storeDetails.instructions_enable"
                  >
                    <div
                      id="instruction-title"
                      v-if="storeDetails.instructions_title"
                    >
                      {{ storeDetails.instructions_title }}
                    </div>
                    <div
                      id="instruction-details"
                      v-if="storeDetails.instructions_message"
                    >
                      {{ storeDetails.instructions_message }}
                    </div>
                  </div>
                  <div v-if="paymentMethod && computedTotal">
                    <CheckoutDetailsHeader ref="paymentMethod"
                      >Payment Method</CheckoutDetailsHeader
                    >
                    <div class="checkout-details-body">
                      <div
                        class="payment-selection-btn"
                        style="display: flex; flex-flow: column"
                        v-for="(payment, index) in paymentMethodsDisplay"
                        :key="index"
                      >
                        <input                          
                          v-model="selectedPayment"
                          type="radio"
                          class="radio_item"
                          :value="payment.value"
                          name="item"
                          :id="`radio-${payment.value}`"
                          @click="isBillingDetails = payment.value === 'CC'"
                        />
                        <label
                          class="label_item d-inline-flex align-items-center"
                          :for="`radio-${payment.value}`"
                        >
                          <img
                            class="pr-2 img-fluid"
                            :src="payment.icon"
                            :alt="payment.value"
                            width="45"
                            :style="{
                              'object-fit': payment.value === 'SHOPEEPAY_XENDIT' ? 'cover' : null,
                              'height': payment.value === 'SHOPEEPAY_XENDIT' ? '25px' : null
                            }"
                          />
                          <span>
                            <div>{{ payment.text }}</div>
                            <small v-if="payment.min_spend > 0">
                              (&#8369; {{ payment.min_spend }} minimum spend requirement)
                            </small>
                          </span>
                          <img
                            src="@/assets/images/check-circle-filled-orange.png"
                            v-if="selectedPayment === payment.value"
                            class="ml-auto"
                            alt="Check"
                          />
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-if="isBillingDetails && computedTotal"
                  class="checkout-details pb-20 mt-20"
                >
                  <CheckoutDetailsHeader ref="billingDetails">
                    Billing Details
                  </CheckoutDetailsHeader>
                  <div class="checkout-details-body">
                    <b-row>
                      <b-col lg="6">
                        <b-form-group
                          label="First Name"
                          label-class="checkout-form-label"
                          label-for="bd-firstName">
                          <b-form-input
                            id="bd-firstName"
                            :type="isBillingDetails ? 'text' : 'hidden'"
                            placeholder="Enter First Name"
                            required
                            v-model="firstName">
                          </b-form-input>
                        </b-form-group>
                      </b-col>
                      <b-col lg="6">
                        <b-form-group
                          label="Last Name"
                          label-class="checkout-form-label"
                          label-for="bd-lastName">
                          <b-form-input
                            id="bd-lastName"
                            :type="isBillingDetails ? 'text' : 'hidden'"
                            placeholder="Enter Last Name"
                            required
                            v-model="lastName">
                          </b-form-input>
                        </b-form-group>
                      </b-col>
                      <b-col lg="12">
                        <b-form-group
                          label="Email Address"
                          label-class="checkout-form-label"
                          label-for="bd-emailAddress">
                          <b-form-input
                            id="bd-emailAddress"
                            :type="isBillingDetails ? 'email' : 'hidden'"
                            placeholder="Enter Email Address"
                            required
                            v-model="email">
                          </b-form-input>
                        </b-form-group>
                      </b-col>
                      <b-col lg="12">
                        <b-form-group
                          label="Mobile Number"
                          label-class="checkout-form-label"
                          label-for="mobileInput">
                          <b-input-group prepend="+63">
                            <input
                              class="form-control appended"
                              id="mobileInput"
                              placeholder="9171234567"
                              required
                              v-model="mobileNumber"
                              maxlength="10"
                              minlength="10"
                              @keydown="isNumber($event)"
                              :type="
                                !isBillingDetails
                                  ? 'hidden'
                                  : isMobile
                                  ? 'number'
                                  : 'text'
                              "
                              pattern="[0-9]*"
                              @keyup="trimNumber('mobileNumber')"
                            />
                          </b-input-group>
                        </b-form-group>
                      </b-col>
                      <b-col lg="12">
                        <b-form-group
                          label="Address Line 1"
                          label-class="checkout-form-label"
                          label-for="bd-address-line-1">
                          <b-form-input
                            id="bd-address-line-1"
                            :type="isBillingDetails ? 'text' : 'hidden'"
                            placeholder="Unit / House No. / Building / Street Name"
                            required
                            v-model="addressLine1">
                          </b-form-input>
                        </b-form-group>
                      </b-col>
                      <b-col lg="12">
                        <b-form-group
                          label="Address Line 2"
                          label-class="checkout-form-label"
                          label-for="bd-address-line-2">
                          <b-form-input
                            id="bd-address-line-2"
                            :type="isBillingDetails ? 'text' : 'hidden'"
                            placeholder="Town / Barangay"
                            v-model="addressLine2">
                          </b-form-input>
                        </b-form-group>
                      </b-col>
                      <b-col lg="6" v-if="!!provinces">
                        <b-form-group
                          label="State/Region"
                          label-class="checkout-form-label"
                          label-for="province">
                          <b-form-select
                            name="provinces"
                            id="province"
                            v-model="selectedProvince"
                            @change="getCities"
                            :required="isBillingDetails"
                            class="cc-form-select">
                            <b-form-select-option
                              v-for="(province, index) in provinces.provinces"
                              :key="index"
                              :value="province.name">
                              {{ province.name }}
                            </b-form-select-option>
                          </b-form-select>
                        </b-form-group>
                      </b-col>
                      <b-col lg="6" v-if="!!cities">
                        <b-form-group
                          label="City"
                          label-class="checkout-form-label"
                          label-for="city">
                          <b-form-select
                            name="cities"
                            id="city"
                            v-model="selectedCity"
                            :required="isBillingDetails"
                            class="cc-form-select">
                            <b-form-select-option
                              v-for="(city, index) in cities.cities"
                              :key="index"
                              :value="city.name"
                            >
                              {{ city.name }}
                            </b-form-select-option>
                          </b-form-select>
                        </b-form-group>
                      </b-col>
                      <b-col lg="6">
                        <b-form-group
                          label="Zip Code"
                          label-class="checkout-form-label"
                          label-for="bd-zip-code">
                          <b-form-input
                            id="bd-zip-code"
                            :type="isBillingDetails ? 'text' : 'hidden'"
                            placeholder="Enter Zip Code"
                            v-model="zipCode"
                            required>
                          </b-form-input>
                        </b-form-group>
                      </b-col>
                      <b-col lg="6">
                        <b-form-group
                          label="Country"
                          label-class="checkout-form-label"
                          label-for="bd-country">
                          <b-form-input
                            id="bd-country"
                            placeholder
                            v-model="country"
                            disabled>
                          </b-form-input>
                        </b-form-group>
                      </b-col>
                    </b-row>
                  </div>
                </div>
                <div class="text-right" v-if="!isMobile">
                  <b-button
                    type="submit"
                    variant="primary"
                    class="py-2 px-4 border-0 mt-4"
                    :style="{
                      backgroundColor: !!webDesign && !!webDesign.design
                        ? `${webDesign.design.main_color} !important`
                        : null,
                      color: !!webDesign && !!webDesign.design
                        ? `${webDesign.design.button_text_color} !important`
                        : null
                    }"
                    :disabled="
                      disabledButton ||
                      isProcessing ||
                      isAwaitingPayment ||
                      minimumSpendNotMetPayMongo ||
                      estoreIsLoading ||
                      hasScheduledFulfillmentError ||
                      minimumSpendNotMetXendit ||
                      isValidAddress
                    ">
                    Proceed to Payment
                  </b-button>
                </div>
              </div>
              <div
                :class="['flex-grow-1', { 'col-lg-5': !isMobile }]"
                v-if="cart"
              >
                <template v-if="isMobile">
                  <div
                    id="checkout-breadcrumbs"
                    class="py-3 px-4 d-flex flex-row align-items-center"
                  >
                    <span
                      :class="[
                        'cursor-pointer',
                        {
                          'text-bold':
                            !isOrderSummaryCollapsed &&
                            intoView === 'deliveryDetails'
                        }
                      ]"
                      @click="
                        intoView = 'deliveryDetails';
                        $root.$emit('scrollTo', 'deliveryDetails');
                      "
                      >Delivery Details</span
                    >
                    <img
                      src="@/assets/images/arrow-down.svg"
                      class="breadcrumbs-arrow mt-1 mx-2"
                    />
                    <span
                      :class="[
                        'cursor-pointer',
                        {
                          'text-bold':
                            !isOrderSummaryCollapsed &&
                            intoView === 'paymentMethod'
                        }
                      ]"
                      @click="
                        intoView = 'paymentMethod';
                        $root.$emit('scrollTo', 'paymentMethod');
                      "
                      >Payment</span
                    >
                  </div>
                  <div
                    class="
                      py-3
                      px-4
                      bg-white
                      border-bottom
                      d-flex
                      align-items-center
                    "
                  >
                    <span class="flex-grow-1 flex-shrink-0">
                      Show order summary
                      <img
                        src="@/assets/images/arrow-down.svg"
                        class="order-summary-collapse"
                        v-b-toggle.order-summary-collapse
                        ref="orderSummaryToggle"
                      />
                    </span>
                    <h5
                      class="mb-0 text-right"
                      :style="{
                        color:
                          webDesign !== null && webDesign.design !== null
                            ? webDesign.design.main_color
                            : '#ff8a00'
                      }"
                    >
                      {{ `Php ${computedTotal.toFixed(2)}` }}
                    </h5>
                  </div>
                </template>
                <b-collapse
                  id="order-summary-collapse"
                  v-model="isOrderSummaryCollapsed"
                >
                  <Cart
                    title="Review and Place Order"
                    type="big"
                    :show-fulfillment="true"
                  />
                  <!-- <template v-if="storeDetails">
                    <template
                      v-if="storeDetails.document_upload_enable == 1"
                    >
                      <DocumentsUpload
                        v-on:getDocuments="getDocuments($event)"
                      />
                    </template>
                  </template> -->
                  <Computation checkoutButton="true" />
                </b-collapse>
              </div>
            </div>
            <template v-if="isMobile">
              <div class="text-center mb-5 px-4">
                <b-button
                  type="submit"
                  variant="primary"
                  class="py-2 px-4 border-0 mt-4"
                  block
                  v-if="!!webDesign && !!webDesign.design"
                  :style="{
                    backgroundColor: `${webDesign.design.main_color} !important`,
                    color: `${webDesign.design.button_text_color} !important`
                  }"
                  :disabled="
                    disabledButton ||
                    isProcessing ||
                    isAwaitingPayment ||
                    minimumSpendNotMetPayMongo ||
                    estoreIsLoading ||
                    hasScheduledFulfillmentError ||
                    isValidAddress
                  "
                >
                  Proceed to Payment
                </b-button>
              </div>
            </template>
          </div>
        </b-form>
      </main>
    </b-overlay>
    <!-- Stocks Modal -->
    <b-modal ref="stocksModal" hide-footer>
      <div class="modal-body aCenter">
        <img src="@/assets/images/info.svg" class="mt-10" />
        <div v-if="errorMessage != null">
          <p v-if="errorMessage.errors.lat != null">
            {{ errorMessage.errors.lat }}
          </p>
          <p v-else-if="errorMessage.errors.message != null">
            {{ errorMessage.errors.message }}
          </p>
          <p v-else-if="errorMessage.errors['file.0'] != null">
            {{ errorMessage.errors["file.0"] }}
          </p>
          <p v-else>
            {{ errorMessage.errors }}
          </p>
        </div>
      </div>
    </b-modal>
    <!-- Start Not Within Radius: for Cart Linking Drop 2
    <b-modal ref="notWithinBranchModal" hide-footer>
      <div class="modal-body aCenter">
        <img src="@/assets/images/info.svg" class="mt-10" />
        <p class="my-3" v-if="estoreErrors">
          {{ estoreErrors.errors.lat }}
        </p>
        <p class="my-3" v-for="(branch, index) in notWithinBranch" :key="index">
          <strong>{{ branch.brand_name }} - {{ branch.branch_name }}</strong>
        </p>
        <p class="my-3">
          If you CONTINUE, the items on your cart from the location(s) above
          will be removed.
        </p>
        <p class="my-3">Select CLOSE to go back to your cart.</p>
        <b-button
          type="button"
          class="btn btn-core text-uppercase mr-3"
          style="
            background-color: transparent !important;
            color: #ff8a00 !important;
          "
          @click="$refs.notWithinBranchModal.hide()"
        >
          CLOSE
        </b-button>
        <b-button
          type="button"
          class="btn btn-core"
          @click="getNotWithinBranchToDelete"
        >
          CONTINUE
        </b-button>
      </div>
    </b-modal>
    End Not Within Radius: for Cart Linking Drop 2 -->
    <MEDPadalaError />
  </div>
</template>

<script>
import $ from "jquery";
import { mapState, mapGetters, mapActions } from "vuex";
import Cart from "@/components/cart/Cart";
import Computation from "@/components/cart/Computation";
// import DocumentsUpload from "@/components/cart/Documents.vue";
import MEDPadalaError from "@/components/cart/MEDPadala/MedPadalaError";
import CheckoutDetailsHeader from "@/components/checkout/CheckoutDetailsHeaderTemplate";
import { ls, paymentMethodDisplayMinSpendValue, paymentMethodDisplayLabel, paymentMethodDisplayIcon } from "@/utilities";

export default {
  components: {
    Cart,
    Computation,
    // DocumentsUpload,
    MEDPadalaError,
    CheckoutDetailsHeader
  },
  computed: {
    ...mapState("merchant", {
      subdomain: state => state.subdomain,
      customerPortalToken: state => state.customerPortalToken,
    }),
    ...mapState("estore", {
      cart: state => state.cart,
      estoreIsLoadingOverlay: state => state.loadingOverlay,
      estoreIsLoading: state => state.loading,
      estoreErrors: state => state.errors,
      checkout: state => state.checkout,
      paynamics: state => state.paynamics,
      webDesign: state => state.webDesign,
      notWithinBranch: state => state.notWithinBranch
    }),
    ...mapGetters({
      transactionId: "estore/transactionId",
      provinces: "customer/provinces",
      cities: "customer/cities",
      medPadalaData: "medpadala/medPadalaData",
      isProcessing: "estore/loading",
      storeDetails: "estore/storeDetails",
      customerToken: "customer/customerToken"
    }),
    paymentMethod: {
      get() {
        if (this.storeDetails && this.storeDetails.payment_type) return this.storeDetails.payment_type
          .filter(payment =>
            payment === 'COD'
            || payment.includes('PAYMONGO')
            || payment.includes('XENDIT')
          );
        else return [];
      },
      set(payment_method) {
        return payment_method;
      }
    },
    paymentMethodsDisplay: {
      get() {
        return this.paymentMethod.map(type => {
          const option = {
            value: type,
            min_spend: paymentMethodDisplayMinSpendValue(type),
            text: paymentMethodDisplayLabel(type),
            icon: paymentMethodDisplayIcon(type)
          };
          return option;
        });
      },
      set(options) {
        return options;
      }
    },
    isGenerikaStaging: {
      get() {
        return this.subdomain === "generikastaging";
      },
      set(status) {
        return status;
      }
    },
    isGenerikaSubdomain() {
      return this.subdomain.includes("generika");
    },
    hasMedPadalaCreditsApplied() {
      return this.medPadalaData && this.cart && this.cart.data.estore_wallets
        ? this.medPadalaData.appliedCredits ||
            this.cart.data.estore_wallets
              .map(wallet => wallet.type)
              .includes("med-padala")
        : false;
    },
    disabledButton: {
      get() {
        return this.storeDetails && this.storeDetails.document
          ? this.documents.length > this.storeDetails.document.no_of_uploads
          : false;
      }
    },
    // hasVoucherError() { Promo Code: for Cart Linking Drop 2
    //   return this.cart && this.cart.voucher_info && this.cart.voucher_info.error
    //     ? true
    //     : false;
    // },
    minimumSpendNotMetPayMongo() {
      if (this.selectedPayment && this.selectedPayment.includes("PAYMONGO")) {
        return this.cart && parseFloat(this.cart.data.computed_total) < 100;
      }
      return false;
    },
    minimumSpendNotMetXendit() {
      if (this.selectedPayment && this.selectedPayment.includes("XENDIT")) {
        const min_spend = this.paymentMethodsDisplay.find(payment => payment.value === this.selectedPayment).min_spend;
        return this.cart && parseFloat(this.cart.computed_total) < min_spend;
      }
      return false;
    },
    // isVoucherCoveredComputedAmt() { Promo Code: for Cart Linking Drop 2
    //   if (!this.hasVoucherError) return false;
    //   return true;
    // },
    computedTotal() {
      if (!this.cart) return 0;

      return this.cart.data.computed_total;
    },
    subTotal() {
      if (!this.cart) return 0;

      return this.cart.data.cart_total_amount;
    },
    medPadalaWallet() {
      if (!this.cart) return null;

      return this.cart.data.estore_wallets.find(
        wallet => wallet.type === "med-padala"
      );
    },
    hasScheduledFulfillmentError() {
      return this.cart && this.cart.data.list
        ? this.cart &&
            this.cart.data.list.some(
              item =>
                (item.delivery_type.toLowerCase().includes("schedule") ||
                  item.delivery_type.toLowerCase().includes("later")) &&
                !item.pickup_datetime
            )
        : false;
    },
    isValidAddress() {
      return (this.lat == null && this.lng == null) || this.invalidLocation;
    }
  },
  created: function() {
    this.getCities();
    this.getProvinces();
    this.isMobile = window.innerWidth < 991;
    this.getCartDetails();
  },
  data() {
    return {
      isBillingDetails: false,
      selectedPayment: null,
      enteredLocation: null,
      house: null,
      business: null,
      notes: "",
      checkedBranch: null,
      checkedBrand: null,
      checkedStock: null,
      checkedProductName: null,
      checker: false,
      errorMessage: null,
      firstName: null,
      lastName: null,
      email: null,
      mobileNumber: null,
      addressLine1: null,
      addressLine2: null,
      selectedCity: null,
      selectedProvince: null,
      zipCode: null,
      country: "PH",
      paymentStatus: null,
      paymentMessage: null,
      isMobile: false,
      isOrderSummaryCollapsed: true,
      intoView: "deliveryDetails",
      documents: [],
      isAwaitingPayment: false,
      emailAddress: null,
      lat: null,
      lng: null,
      contact_info: {
        first_name: null,
        last_name: null,
        email: null,
        mobile_number: null
      },
      invalidLocation: false
    };
  },
  methods: {
    ...mapActions({
      checkPaymentStatus: "estore/checkPaymentStatus"
    }),
    setPlace(place) {
      try {
        this.invalidLocation = false;
        this.lat = place.geometry.location.lat();
        this.lng = place.geometry.location.lng();
        this.enteredLocation = place.formatted_address;
        this.updateLocation();
      } catch (error) {
        this.invalidLocation = true;
      }
    },
    getDocuments(files) {
      this.documents = files;
    },
    async checkBranches() {
      let request = {
        header: {
          Authorization: this.token,
          "X-Merchant-Token": this.customerPortalToken
        },

        payload: {
          latitude: this.lat,
          longitude: this.long
          // distance: this.coordinates.distance
        }
      };

      let withinRadius = true;
      await this.$store.dispatch("estore/checkBranches", request).then(() => {
        if (this.notWithinBranch.length >= 1) {
          this.$refs.notWithinBranchModal.show();
          withinRadius = false;
        } else {
          withinRadius = true;
        }
      });
      return withinRadius;
    },
    async proceedCheckout() {
      if (this.customerPortalToken != null) {

        //validate branches: for Cart Linking Drop 2
        // let withinRadius = await this.checkBranches();
        let withinRadius = true;

        if (withinRadius) {
          //validate documents
          let allowedTypes = this.storeDetails.document_upload_types;

          let newDocuments = [];
          this.documents.forEach(file => {
            if (
              allowedTypes.includes(
                file.name.substr(file.name.lastIndexOf(".") + 1).toUpperCase()
              )
            ) {
              if (
                this.documents.length <=
                this.storeDetails.document.no_of_uploads
              ) {
                newDocuments.push(file);
              } else {
                this.errorMessage = "Maximum no. of uploads reached.";
                // this.showErrorModal();
              }
            }
          });

          // Voucher
          let voucherCode = "";

          if (this.cart.data.voucher_info) voucherCode = this.cart.data.voucher_info.code;

          const form_data = new FormData();
          if (newDocuments.length > 0) {
            for (let i = 0; i < newDocuments.length; i++) {
              form_data.append("file[]", newDocuments[i]);
            }
          }
          form_data.append("customer_name", `${this.contact_info.first_name} ${this.contact_info.last_name}`);
          form_data.append("customer_email", this.contact_info.email);
          form_data.append("mobile_number", `0${this.contact_info.mobile_number}`);
          form_data.append("payment_type", this.selectedPayment);

          // form_data.append("delivery_address", this.enteredLocation);
          // form_data.append("delivery_address_details", `${this.house}, ${this.business}`);
          form_data.append("delivery_address", `${this.house}, ${this.business}, ${this.enteredLocation}`);
          form_data.append("voucher_code", voucherCode);
          form_data.append("lat", this.lat);
          form_data.append("lng", this.lng);
          form_data.append("ordered_via", "web");
          form_data.append("notes", this.notes);

          // MEDPadala PIN
          if (this.hasMedPadalaCreditsApplied) {
            form_data.append("pin", ls.get("MEDPadalaPin"));
          }

          if (this.checker == false || this.errorMessage == null) {
            const { checkout_uuid } = this.$route.params;
            let request = {
              checkout_uuid: checkout_uuid,
              header: {
                // Authorization: this.customerToken,
                "X-Merchant-Token": this.customerPortalToken
              },
              payload: form_data
            };

            let _this = this;
            await this.$store.dispatch("estore/checkout", request).then(() => {
              this.$store.commit("customer/SET_CUSTOMER_CONTACT", {
                email: this.contact_info.email,
                mobile_number: this.contact_info.mobile_number
              });
              this.$store.commit("customer/SET_ADDRESS", {
                delivery_address: _this.enteredLocation,
                delivery_address_details: `${_this.house}, ${_this.business}`
              });
              if (
                !_this.estoreErrors &&
                _this.selectedPayment === "CC_PAYMONGO"
              ) {
                this.$router.push({
                  name: "CardDetails",
                  query: {
                    orderId: _this.checkout.order_transaction_uuid,
                    checkoutId: checkout_uuid
                  }
                });
              } else if (
                !_this.estoreErrors &&
                _this.selectedPayment === "GCASH_PAYMONGO"
              ) {
                this.$router.push({
                  name: "PayMongoDetails",
                  params: { paymongo_type: "gcash" },
                  query: {
                    orderId: _this.checkout.order_transaction_uuid,
                    checkoutId: checkout_uuid
                  }
                });
              } else if (
                !_this.estoreErrors &&
                _this.selectedPayment === "GRAB_PAY_PAYMONGO"
              ) {
                this.$router.push({
                  name: "PayMongoDetails",
                  params: { paymongo_type: "grabpay" },
                  query: {
                    orderId: _this.checkout.order_transaction_uuid,
                    checkoutId: checkout_uuid
                  }
                });
              } else if (!_this.estoreErrors && _this.selectedPayment.includes("XENDIT")) {
                window.open(_this.checkout.invoice_url, "_self", "rel=noopener");
              } else if (_this.estoreErrors == null) {
                _this.isAwaitingPayment = false;
                _this.$router.push({ name: 'Orders', params: { order_id: this.checkout.order_transaction_uuid } });
              } else {
                _this.isAwaitingPayment = false;
                if (
                  Object.keys(_this.estoreErrors.errors).includes(
                    "is_medpadala_error"
                  ) &&
                  _this.estoreErrors.errors.is_medpadala_error
                ) {
                  this.$root.$emit("bv::show::modal", "medpadala-error-modal");
                } else {
                  _this.errorMessage = _this.estoreErrors;
                  _this.showErrorModal();
                }
              }
            });
          }
        }
      }
    },
    // Start Not Within Radius: for Cart Linking Drop 2
    // getNotWithinBranchToDelete() {
    //   for (let index = 0; index < this.notWithinBranch.length; index++) {
    //     this.removeAll(this.notWithinBranch[index].branch_uuid);
    //   }
    //   this.$refs.notWithinBranchModal.hide();
    //   this.$router.push({ name: "Cart" });
    // },
    // async removeAll(branch_uuid) {
    //   let items = [];

    //   for (let index = 0; index < this.cart.list.length; index++) {
    //     if (branch_uuid == this.cart.list[index].branch_uuid) {
    //       items[index] = this.cart.list[index].items;
    //     }

    //     if (this.customerPortalToken != null) {
    //       let request = {
    //         header: {
    //           Authorization: this.token,
    //           "X-Merchant-Token": this.customerPortalToken
    //         },
    //         payload: {
    //           cart_items: items,
    //           cart_count: this.cart.cart_count,
    //           cart_total_amount: this.cart.cart_total_amount
    //         }
    //       };
    //       let _this = this;
    //       await this.$store.dispatch("estore/removeItem", request).then(() => {
    //         _this.$store.commit("estore/SET_CART");
    //       });
    //     }
    //   }
    // },
    // End Not Within Radius: for Cart Linking Drop 2
    async getCities() {
      let request = {
        header: {
          "X-Merchant-Token": this.customerPortalToken
        },
        payload: {
          province: this.selectedProvince
        }
      };
      await this.$store.dispatch("customer/getCities", request);
    },
    async getProvinces() {
      let request = {
        header: {
          "X-Merchant-Token": this.customerPortalToken
        }
      };
      let _this = this;
      await this.$store.dispatch("customer/getProvinces", request).then(() => {
        if (_this.estoreErrors == null) {
          _this.getCities();
        }
      });
    },
    onProceedCheckout() {
      if (this.cart) {
        if (this.computedTotal === 0) {
          this.isAwaitingPayment = true;
          this.isBillingDetails = false;

          if (
            this.cart &&
            this.cart.data.voucher_info &&
            this.cart.data.voucher_info.code
          )
            this.selectedPayment = "COD";

          if (
            this.medPadalaWallet &&
            this.medPadalaWallet.amount >= this.subTotal
          )
            this.selectedPayment = "MEDPADALA-WALLET";

          this.proceedCheckout();

          return;
        }

        if (this.selectedPayment) {
          if (
            !this.isGenerikaSubdomain &&
            this.selectedPayment === "CC_PAYMONGO"
          ) {
            this.proceedCheckout();
          } else if (
            !this.isGenerikaSubdomain &&
            this.selectedPayment === "GCASH_PAYMONGO"
          ) {
            this.proceedCheckout();
          } else {
            this.isAwaitingPayment = true;
            this.proceedCheckout();
          }
        } else alert("Please select a payment method.");
      }
    },
    showErrorModal() {
      this.$refs["stocksModal"].show();
    },
    isNumber(evt) {
      evt = evt ? evt : window.event;
      var charCode = evt.which ? evt.which : evt.keyCode;

      const maxlength = 10;
      const fieldLength = evt.target.value.length;
      if (
        (charCode >= 48 && charCode <= 57 && fieldLength < maxlength) ||
        charCode === 8
      ) {
        return true;
      } else {
        evt.preventDefault();
      }
    },
    trimNumber(ref) {
      setTimeout(() => {
        this[ref] =
          this[ref] !== null ? this[ref].replace(/\./g, "") : this[ref];
      }, 300);
    },
    async getCartDetails() {
      const { checkout_uuid } = this.$route.params;
      const request = {
        checkout_uuid: checkout_uuid,
        header: {
          "X-Merchant-Token": this.customerPortalToken
        }
      }
      await this.$store.dispatch("estore/viewCart", request);
      if (!this.estoreErrors) await this.$store.dispatch("customer/getCustomerToken", request);
      const customerInfo = ls.get('customer_info');
      if (customerInfo) {
        Object.keys(customerInfo)
          .map(key => {
            if (key === 'mobile_number' && Number(customerInfo[key].charAt(0)) === 0) this.contact_info[key] = customerInfo[key].substring(1);
            else this.contact_info[key] = customerInfo[key];
          });
      }
    },

    async updateLocation () {
      const { checkout_uuid } = this.$route.params;
      const request = {
        header: {
          "X-Merchant-Token": this.customerPortalToken
        },
        payload: {
          longitude: this.lng,
          latitude: this.lat
        },
        checkout_uuid: checkout_uuid,
      };

      await this.$store.dispatch("estore/updateLocation", request).then(async () => {
        this.$store.commit("estore/setHideDeliveryFee", false);
        await this.getCartDetails();
        this.$root.$emit("updateHideDeliveryFee", false);
        this.$root.$emit("updateHideDeliveryFee2", false);
        
      });
    }
  },
  mounted() {
    // Change background to gray
    $("body").css({ backgroundColor: "#f4f5f8" });
    window.addEventListener("resize", () => {
      this.isMobile = window.innerWidth < 991;
    });
    this.$root.$on("scrollTo", intoView => {
      this.$refs[intoView].$el.scrollIntoView({ behavior: "smooth" });
    });
  },
  beforeDestroy() {
    this.$root.$off("scrollTo");
  },
  watch: {
    isMobile(mobile) {
      this.isOrderSummaryCollapsed = !mobile;
    },
    isBillingDetails(isTrue) {
      if (isTrue) {
        this.getCities();
        this.getProvinces();
      }
    }
  }
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
@import "~@/assets/styles/views/checkout/_base.scss";
.outline-0 {
  outline: 0;
}

.enter-location-container {
  .form-control {
    .pac-target-input {
      font-size: 16px;
      font-weight: 600;
      color: #2F3941;
    }
  }
}

.radio_item {
  display: none !important;
}

.label_item {
  border: 1px solid #d8dcde;
  border-radius: 5px;
  padding: 10px;
  margin-bottom: 15px;
  height: 50px;
  width: 100%;
}

.radio_item:checked + label {
  border: 2px solid orange;
}
label {
  cursor: pointer;
}

#paymentrequest {
  display: none;
}
.order-summary-collapse {
  outline: 0;
  transition: all ease 0.2s;
  &.not-collapsed {
    transform: rotate(180deg);
  }
}
#checkout-breadcrumbs {
  .breadcrumbs-arrow {
    transform: rotate(-90deg);
    height: 18px;
  }
  .cursor-pointer {
    cursor: pointer;
    &.text-bold {
      font-weight: 500;
    }
  }
}
input[type="number"] {
  font-weight: 300;
  height: 45px;
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    -moz-appearance: textfield;
    appearance: none;
    margin: 0;
  }
}
#instruction-container {
  margin: 20px 30px;
  #instruction-title {
    font-size: 16px;
    font-weight: 600;
    padding-bottom: 12px;
    border-bottom: 1px solid #d8dcde;
  }
  #instruction-details {
    font-size: 14px;
    padding-top: 12px;
  }
}
.cc-form-select {
  background: #fff url('~@/assets/images/chevron-down.png') no-repeat center right / 30px;
}
</style>

<style lang="scss">
#checkout-form {
  .checkout-form-label {
    font-weight: 400;
    font-size: 16px;
    color: #68737d;
  }

  .checkout-form-required::after {
    content: " *";
    color: red;
    font-size: 16px;
  }
}
</style>
